import type { TFunction } from 'i18next';
import type { KeyProcessMeasuresData } from '../../../../../helpers/process-analysis-shared/process-analysis-shared.type';

/**
 * Formats the key measures data for the key measures section
 *
 * @param {KeyProcessMeasuresData | undefined} keyMeasuresData - The key measures data to format
 * @param {number | null} calculatedScopeChange - The calculated scope change percentage
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {object[]} The formatted key measures data
 */
function formatKeyMeasuresData(
  keyMeasuresData: KeyProcessMeasuresData | undefined,
  calculatedScopeChange: number | null,
  t: TFunction,
): {
  id: string;
  name: string;
  value: string;
  description: string;
}[] {
  if (!keyMeasuresData) return [];

  // Use calculated scope change (same logic as Work Pace chart)
  const scopeValue = calculatedScopeChange != null ? `${calculatedScopeChange}%` : '--';

  return [
    {
      id: 'strategy',
      name: `${t('process_analysis.assessment.key_measures.strategy')}`,
      value: keyMeasuresData.strategy != null ? `${keyMeasuresData.strategy}%` : '--',
      description: `% Tied to an ${t('common.epic').toLowerCase()}`,
    },
    {
      id: 'complexity',
      name: `${t('process_analysis.assessment.key_measures.complexity')}`,
      value: keyMeasuresData.complexity != null ? `${keyMeasuresData.complexity}` : '--',
      description: `Avg. estimate for pointed ${t('common.work_units').toLowerCase()}`,
    },
    {
      id: 'quality',
      name: `${t('process_analysis.assessment.key_measures.quality')}`,
      value: keyMeasuresData.quality != null ? `${keyMeasuresData.quality}%` : '--',
      description: 'Bugs to share',
    },
    {
      id: 'scope_creep',
      name: `${t('process_analysis.assessment.key_measures.scope_creep')}`,
      value: scopeValue,
      description: `${t('common.work_units')} +/- to plan`,
    },
    {
      id: 'readiness',
      name: `${t('process_analysis.assessment.key_measures.readiness')}`,
      value: keyMeasuresData.readiness != null ? `${keyMeasuresData.readiness}%` : '--',
      description: `Defined ${t('common.work_units').toLowerCase()}`,
    },
    {
      id: 'independence',
      name: `${t('process_analysis.assessment.key_measures.independence')}`,
      value: keyMeasuresData.independence != null ? `${keyMeasuresData.independence}%` : '--',
      description: `${t('common.work_units')} with zero dependency`,
    },
    {
      id: 'initial_committed_tasks',
      name: `${t('process_analysis.assessment.key_measures.initial_committed_tasks')}`,
      value: keyMeasuresData.initial_committed_tasks != null ? `${keyMeasuresData.initial_committed_tasks}` : '--',
      description: `${t('common.work_units')} committed at start`,
    },
    {
      id: 'initial_committed_points',
      name: `${t('process_analysis.assessment.key_measures.initial_committed_points')}`,
      value: keyMeasuresData.initial_committed_points != null ? `${keyMeasuresData.initial_committed_points}` : '--',
      description: 'Points committed at start',
    },
  ];
}

/**
 * Checks if the health score section is enabled, -1 is considered disabled
 *
 * @param {number | undefined} score - The score to check
 * @returns {boolean} True if the health score section is enabled, false otherwise
 */
function isHealthScoreSectionEnabled(score: number | undefined): boolean {
  if (score === undefined || Number.isNaN(score) || score === -1) return false;
  return true;
}

export { formatKeyMeasuresData, isHealthScoreSectionEnabled };
